import { zodResolver } from '@hookform/resolvers/zod';
import { TFunction } from 'i18next';
import { useForm, SubmitHandler } from 'react-hook-form';
import z from 'zod';
import { QUERY_KEYS } from '../constants';
import { useUpdateBalance, FormValues } from '../mutations/useUpdateBalance';
import { TRANSACTION_TYPE, WALLET_TYPE } from '../types/enums';
import { useModal } from './useDialog';
import { useInvalidateQuery } from './useInvalidateQuery';
import { useRoles } from './useRoles';
import { UserBalance } from '../@types';
import useLocalization from './useLocalization';

const getTransactionType = (amount: number) => (amount > 0 ? TRANSACTION_TYPE.DEPOSIT : TRANSACTION_TYPE.WITHDRAWAL);

const getUpdateCreditsSchema = (t: TFunction) =>
  z.object({
    amount: z
      .string({
        error: t('register.validation.requiredField'),
      })
      .trim()
      .min(1, t('register.validation.requiredField'))
      .refine((value) => !Number.isNaN(Number(value)), { error: t('invalidNumber') }),
  });

type UpdateCreditsFormValues = {
  amount: string;
};

export const useUpdateCredits = (userBalanceData: UserBalance, handleUpdate: () => void) => {
  const { isAgent } = useRoles();
  const invalidateQuery = useInvalidateQuery();
  const { closeModal } = useModal();
  const { t } = useLocalization();
  const { mutate: updateCredits } = useUpdateBalance();

  const {
    formState: { isSubmitting, errors },
    control,
    handleSubmit,
  } = useForm<UpdateCreditsFormValues>({
    resolver: zodResolver(getUpdateCreditsSchema(t)),
    defaultValues: {
      amount: '',
    },
    mode: 'onBlur',
  });

  const onSubmit: SubmitHandler<UpdateCreditsFormValues> = (data) => {
    const parsedAmount = Number(data.amount);

    const payload: FormValues = {
      userId: userBalanceData.userId,
      isForAgentPayment: !isAgent,
      transactionType: getTransactionType(parsedAmount),
      otherTransactionType: getTransactionType(parsedAmount),
      amount: Math.abs(parsedAmount),
      walletType: WALLET_TYPE.MAIN,
      source: 'sportsbook',
    };

    updateCredits(payload, {
      onSettled: () => {
        handleUpdate();
        invalidateQuery([QUERY_KEYS.balance]);
        closeModal();
      },
    });
  };
  return {
    onSubmit,
    isSubmitting,
    control,
    handleSubmit,
    errors,
  };
};
